/* Interactions ported from the Claude Design homepage.
   The design is React; these are the same state transitions in plain JS, using
   the style values read out of its source rather than eyeballed.

   Everything degrades to the rendered static state: if this file never runs,
   the page still shows its default panel, default billing and closed FAQ rows.
   Nothing is hidden behind a class this script is responsible for adding. */
(function () {
  'use strict';

  var EASE = 'cubic-bezier(.23,1,.32,1)';
  var reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  var narrow = function () { return window.innerWidth < 860; };

  var $ = function (sel, root) { return [].slice.call((root || document).querySelectorAll(sel)); };
  var byState = function (v, root) { return $('[data-st="' + v + '"]', root); };

  /* Panes share one grid cell; only visibility and transform differ.
     Design: pane(active) */
  function showPane(el, active) {
    el.style.opacity = active ? '1' : '0';
    el.style.visibility = active ? 'visible' : 'hidden';
    el.style.transform = active ? 'none' : 'translateY(10px)';
    el.style.pointerEvents = active ? 'auto' : 'none';
  }

  /* ── Hero: Founders / Media buyers ─────────────────────────────────────
     Design: audTab(active) + pane(active) */
  function audience() {
    var tabs = byState('aud'), panes = byState('aud-pane');
    if (tabs.length < 2 || panes.length < 2) return;

    function pick(n) {
      tabs.forEach(function (t, i) {
        var on = i === n;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.style.border = '1px solid ' + (on ? 'rgba(255,255,255,.6)' : 'rgba(255,255,255,.2)');
        t.style.background = on ? 'rgba(255,255,255,.12)' : 'transparent';
        t.style.color = on ? '#fff' : 'rgba(255,255,255,.62)';
      });
      panes.forEach(function (p, i) { showPane(p, i === n); });
    }
    tabs.forEach(function (t, i) { t.addEventListener('click', function () { pick(i); }); });
  }

  /* ── Surfaces: See / Diagnose / Act / Ask ──────────────────────────────
     Design: tabs[].style — background + colour only. */
  function features() {
    var tabs = byState('feat'), panes = byState('feat-pane');
    if (!tabs.length) return;

    function pick(n) {
      tabs.forEach(function (t, i) {
        var on = i === n;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.style.background = on ? 'rgba(107,47,191,.08)' : 'none';
        t.style.color = on ? '#0D0D0D' : 'rgba(13,13,13,.5)';
      });
      panes.forEach(function (p, i) {
        var on = i === n;
        // one pane ships as display:none rather than hidden; normalise both
        p.style.display = 'block';
        showPane(p, on);
      });
    }
    tabs.forEach(function (t, i) { t.addEventListener('click', function () { pick(i); }); });
  }

  /* ── Safety: four guards, auto-rotating ────────────────────────────────
     Design: 5200ms progress bar, pauses off-screen via IntersectionObserver,
     and stops permanently once the visitor picks one. */
  function safety() {
    var tabs = byState('guard'), panes = byState('guard-pane');
    if (!tabs.length) return;
    var at = 0, timer = null, manual = false;

    function pick(n) {
      at = n;
      tabs.forEach(function (t, i) {
        var on = i === n;
        t.style.border = '1px solid ' + (on ? 'rgba(255,255,255,.22)' : 'rgba(255,255,255,.09)');
        t.style.background = on ? 'rgba(255,255,255,.06)' : 'transparent';
        var bar = t.querySelector('[style*="width"]');
        if (bar && bar.parentElement !== t) {
          bar.style.transition = on ? 'width 5200ms linear' : 'width 200ms ' + EASE;
          bar.style.width = on ? '100%' : '0%';
        }
      });
      panes.forEach(function (p, i) { showPane(p, i === n); });
    }

    function start() { if (!manual && !reduce && !timer) timer = setInterval(function () { pick((at + 1) % tabs.length); }, 5200); }
    function stop() { clearInterval(timer); timer = null; }

    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { manual = true; stop(); pick(i); });
    });

    var host = tabs[0].closest('section');
    if (host && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (es) {
        es.forEach(function (e) { e.isIntersecting ? start() : stop(); });
      }, { threshold: 0.25 }).observe(host);
    } else {
      start();
    }
  }

  /* ── Demo: card stack fans out on hover ────────────────────────────────
     Design: stackCard(i, open, narrow) — disabled on narrow, where the
     lower cards are not rendered at all. */
  var REST = [{ x: 0, y: 0, r: 0 }, { x: -22, y: -34, r: -3.2 }, { x: -42, y: -66, r: -6.2 }];
  var OUT = [{ x: 26, y: 42, r: 1.6 }, { x: -8, y: -18, r: -2 }, { x: -44, y: -82, r: -5 }];

  function stack() {
    var cards = byState('stack-card');
    if (cards.length < 3) return;
    var wrap = cards[0].parentElement;

    function set(open) {
      if (narrow()) return;
      cards.forEach(function (c, i) {
        var p = (open ? OUT : REST)[i];
        c.style.transform = 'translate(' + p.x + 'px, ' + p.y + 'px) rotate(' + p.r + 'deg)';
      });
    }
    wrap.addEventListener('mouseenter', function () { set(true); });
    wrap.addEventListener('mouseleave', function () { set(false); });
  }

  /* ── Pricing: monthly / annual ─────────────────────────────────────────
     Design: price = monthly === 0 ? 'Free' : (annual ? round(m*10/12/50)*50 : m) */
  function billing() {
    var btns = byState('bill'), prices = byState('price');
    if (btns.length < 2) return;

    function pick(mode) {
      var isAnnual = mode === 'annual';
      btns.forEach(function (b) {
        var on = b.getAttribute('data-v') === mode;
        b.style.background = on ? '#6B2FBF' : 'transparent';
        b.style.color = on ? '#fff' : '#555';
      });
      prices.forEach(function (p) {
        var m = parseInt(p.getAttribute('data-m'), 10);
        var a = parseInt(p.getAttribute('data-a'), 10);
        var target = p.querySelector('.sc-interp') || p;
        target.textContent = m === 0 ? 'Free' : (isAnnual ? a : m).toLocaleString('en-US');
        var cadence = p.parentElement && p.parentElement.querySelector('span:last-child .sc-interp');
        if (cadence && m !== 0) cadence.textContent = isAnnual ? 'EGP/mo, billed yearly' : 'EGP/mo';
      });
    }
    btns.forEach(function (b) {
      b.addEventListener('click', function () { pick(b.getAttribute('data-v')); });
    });
  }

  /* ── FAQ: category filter + accordion ──────────────────────────────────
     Design: openFaq resets to -1 whenever the category changes. */
  function faq() {
    var cats = byState('faq-cat'), qs = byState('faq-q'), panels = byState('faq-panel');
    if (!qs.length) return;
    var open = -1;

    function setOpen(n) {
      open = n;
      qs.forEach(function (q, i) {
        var isOpen = i === n;
        var row = q.closest('[style*="border"]') || q.parentElement;
        if (row) {
          row.style.border = '1px solid ' + (isOpen ? 'rgba(107,47,191,.35)' : 'rgba(13,13,13,.12)');
          row.style.background = isOpen ? 'rgba(107,47,191,.03)' : 'transparent';
        }
        var icon = q.querySelector('span[style*="rotate"], span[style*="color"]');
        if (icon) {
          icon.style.color = isOpen ? '#6B2FBF' : '#8A8A8A';
          icon.style.transform = isOpen ? 'rotate(135deg)' : 'none';
        }
      });
      panels.forEach(function (p, i) {
        var isOpen = i === n;
        p.style.gridTemplateRows = isOpen ? '1fr' : '0fr';
        p.style.opacity = isOpen ? '1' : '0';
        p.style.paddingBottom = isOpen ? '20px' : '0px';
      });
    }

    qs.forEach(function (q, i) {
      q.addEventListener('click', function () { setOpen(open === i ? -1 : i); });
    });

    cats.forEach(function (c) {
      c.addEventListener('click', function () {
        var label = c.textContent.trim();
        cats.forEach(function (o) {
          var on = o === c;
          o.style.border = '1px solid ' + (on ? '#6B2FBF' : 'rgba(13,13,13,.16)');
          o.style.background = on ? '#6B2FBF' : 'transparent';
          o.style.color = on ? '#fff' : '#555';
        });
        qs.forEach(function (q, i) {
          var row = q.closest('li,div[style*="border"]') || q.parentElement;
          var cat = (q.getAttribute('data-cat') || '').trim();
          var show = label === 'All' || !cat || cat === label;
          if (row) row.style.display = show ? '' : 'none';
        });
        setOpen(-1);
      });
    });
  }

  /* ── Count-up ──────────────────────────────────────────────────────────
     Design: runCounters() eases 0 → target. The final value is already in the
     DOM, so this only ever replaces a correct number with the same one. */
  function counters() {
    var els = $('.st-count');
    if (!els.length || reduce || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        run(e.target);
      });
    }, { threshold: 0.4 });
    els.forEach(function (el) { io.observe(el); });

    function run(el) {
      var to = parseFloat(el.getAttribute('data-to'));
      var dec = parseInt(el.getAttribute('data-dec') || '0', 10);
      var pre = el.getAttribute('data-prefix') || '';
      var suf = el.getAttribute('data-suffix') || '';
      if (isNaN(to)) return;
      var dur = 1300, start = performance.now();
      requestAnimationFrame(function tick(now) {
        var t = Math.min(1, (now - start) / dur);
        var v = to * (1 - Math.pow(1 - t, 3));
        el.textContent = pre + (dec ? v.toFixed(dec) : Math.round(v).toLocaleString('en-US')) + suf;
        if (t < 1) requestAnimationFrame(tick);
      });
    }
  }


  /* ── Walkthrough: sticky six-step scroll sequence ──────────────────────
     This is the page's main scroll effect and it is JS-driven in the design,
     not CSS — which is why porting the markup alone left it inert.

     Design (componentDidMount): on scroll, rAF-throttled,
       idx = clamp(0..5, floor((innerHeight/2 - colTop) / (colHeight / 6)))
     Each step's number, title, body and mechanism note change colour, the
     stacked screenshot panes cross-fade, the progress bar fills to
     ((step+1)/6)*100%, and the frame's URL label follows the step. */
  var WALK_URLS = [
    'app.scaletrick.com/overview',
    'app.scaletrick.com/meta-ads',
    'app.scaletrick.com/meta-ads/funding',
    'app.scaletrick.com/campaign-builder',
    'app.scaletrick.com/assistant',
    'app.scaletrick.com/audit'
  ];

  function walkthrough() {
    var steps = byState('wt-step'), panes = byState('wt-pane');
    var bar = byState('wt-bar')[0], url = byState('wt-url')[0];
    if (steps.length !== 6 || panes.length !== 6) return;

    var col = steps[0].parentElement;
    var at = -1, ticking = false;

    function paint(n) {
      if (n === at) return;
      at = n;
      steps.forEach(function (step, i) {
        var on = i === n;
        var kids = step.children;
        if (kids[0]) kids[0].style.color = on ? '#C39BFF' : 'rgba(255,255,255,.28)';
        if (kids[1]) kids[1].style.color = on ? '#fff' : 'rgba(255,255,255,.3)';
        if (kids[2]) kids[2].style.color = on ? 'rgba(255,255,255,.74)' : 'rgba(255,255,255,.24)';
        if (kids[3]) {
          kids[3].style.color = on ? '#fff' : 'rgba(255,255,255,.24)';
          kids[3].style.borderLeftColor = on ? '#6B2FBF' : 'rgba(255,255,255,.14)';
        }
      });
      panes.forEach(function (p, i) { showPane(p, i === n); });
      if (bar) bar.style.width = ((n + 1) / 6 * 100) + '%';
      if (url) url.textContent = WALK_URLS[n];
    }

    function measure() {
      var r = col.getBoundingClientRect();
      var mid = window.innerHeight / 2;
      var idx = Math.floor((mid - r.top) / (r.height / 6));
      paint(Math.max(0, Math.min(5, idx)));
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; measure(); });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    /* A backgrounded tab pauses rAF, so a frame queued on the way out never
       runs and `ticking` would stay true for the rest of the session — every
       later scroll silently ignored. Clear it and resync on the way back in. */
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible') return;
      ticking = false;
      at = -1;
      measure();
    });

    measure();
  }

  function init() {
    [audience, features, safety, stack, billing, faq, counters, walkthrough].forEach(function (fn) {
      try { fn(); } catch (err) { /* one broken behaviour must not kill the rest */ }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
